import React from 'react';

// Drop-in replacement for React.lazy that survives stale deployments.
//
// After a new build is deployed, the old hashed *.chunk.js files may no longer
// exist on the server. A browser tab still running the previous build then 404s
// when it tries to code-split a lazy route, which surfaces as a ChunkLoadError.
//
// lazyRetry() handles this in two stages:
//   1. Retry the dynamic import a few times (covers transient network blips /
//      a chunk that is still propagating to the CDN).
//   2. If it keeps failing, do a single hard reload so the browser fetches the
//      fresh index.html + current chunk hashes. A sessionStorage guard ensures
//      we only force-reload once, so a genuinely missing chunk cannot spin the
//      page in an infinite reload loop.
const RELOAD_GUARD_KEY = 'lazy-retry-reloaded-at';
const RELOAD_WINDOW_MS = 60 * 1000;

const isChunkLoadError = error => {
  if (!error) return false;
  const message = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /is not executable, and strict MIME type checking is enabled/i.test(message) ||
    // A stale main bundle can resolve a module id that no longer exists in a
    // freshly deployed chunk, so __webpack_require__ calls `.call()` on
    // undefined. Same root cause as a ChunkLoadError — a version-skewed build.
    /Cannot read properties of undefined \(reading 'call'\)/i.test(message)
  );
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const lazyRetry = (importFn, { retries = 2, interval = 500 } = {}) =>
  React.lazy(() =>
    (async () => {
      let lastError;
      for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
          const module = await importFn();
          // Success: allow a future stale deploy to force-reload again.
          window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
          return module;
        } catch (error) {
          lastError = error;
          if (!isChunkLoadError(error)) throw error;
          if (attempt < retries) await wait(interval * (attempt + 1));
        }
      }

      // Retries exhausted on a chunk error → the deployed build changed under us.
      const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last > RELOAD_WINDOW_MS) {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        window.location.reload();
        // Return a never-resolving promise so React keeps showing the Suspense
        // fallback while the reload takes over, instead of flashing an error.
        return new Promise(() => {});
      }

      throw lastError;
    })(),
  );

export default lazyRetry;
